import { getCandidateProcess, HostCandidateQueryKeys, setCourseCompleted } from 'flux/clients/candidate';
import { sendVerificationCode, verifyCode } from 'modules/host-profile/clients/VerificationClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {useAccount} from "~/authentication/store";

const CACHE_TIME = 1000 * 60 * 15;
const HostProcessKey = "progress"

function useCandidateProcess() {
  const { account } = useAccount();
  const accountId = account?.id;
  const queryClient = useQueryClient();

  const processQuery = useQuery(
    [...HostCandidateQueryKeys, HostProcessKey, accountId],
    async () => getCandidateProcess(accountId),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
      cacheTime: CACHE_TIME,
      retry: 0,
      enabled: !!accountId,
    }
  );

  const setCourseCompletedMutation = useMutation(
    (courseId: string) => setCourseCompleted(account?.id, courseId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...HostCandidateQueryKeys, HostProcessKey, account?.id],
          refetchType: 'active'
        });
        queryClient.invalidateQueries({
          queryKey: ['potential-itineraries', account?.id ?? ''],
          refetchType: 'all'
        });
      },
      retry: 2
    }
  );

  const sendVerificationCodeMutation = useMutation(
    (body: { type: 'email' | 'phone'; value: string }) => {
      return sendVerificationCode(account?.id, body.type, body.value);
    },
    { retry: 0 }
  );

  const verifyCodeMutation = useMutation(
    (body: { verificationId: string; code: string }) => verifyCode(account?.id, body.verificationId, body.code),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([...HostCandidateQueryKeys, HostProcessKey, account?.id], { refetchType: 'active' });
      },
      retry: 2
    }
  );

  return {
    isCandidate: !!account && !!processQuery.data,
    candidateProcess: processQuery.data,
    loading: processQuery.isLoading || processQuery.data === undefined,
    reallyLoading: processQuery.isLoading,
    isSendingCode: sendVerificationCodeMutation.isLoading,
    setCourseCompleted: (courseId: string) => setCourseCompletedMutation.mutateAsync(courseId),
    sendVerificationCode: (type: 'email' | 'phone', value: string) => sendVerificationCodeMutation.mutateAsync({
      type,
      value
    }),
    verifyCode: (verificationId: string, code: string) => verifyCodeMutation.mutateAsync({ verificationId, code }),
    sendingCodeError: sendVerificationCodeMutation.error,
    verifyingCodeError: verifyCodeMutation.error
  }
}

export default useCandidateProcess;
